import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "web/react/emo/button";
import { Modal } from "web/react/emo/modal";
import { Text } from "web/react/emo/text";
import { View } from "web/react/emo/view";
import { useAboveFold } from "web/react/hooks/use-above-fold/use-above-fold";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { OOSRetailerInfoSerializer } from "web/types/serializers";
import * as styles from "./previously-available-from.css";
import { RetailerCard } from "./retailer-card";

const MIN_RETAILER_DISPLAY_COUNT = 3;

interface PreviouslyAvailableFromProps {
    retailersList: OOSRetailerInfoSerializer[];
    uniqueRetailerCount?: number;
    productId: string;
    Button?: React.ComponentType;
    isModal: boolean;
}

const PreviouslyAvailableFrom = React.forwardRef<HTMLDivElement, PreviouslyAvailableFromProps>(
    (
        { retailersList, uniqueRetailerCount, productId, Button, isModal },
        ref
    ): React.ReactElement => (
        <div
            className={clsx(styles.previouslyAvailableFromContainer, {
                [styles.previouslyAvailableFromModal]: isModal,
            })}
            data-testid={`previously-available-from${isModal ? "-modal" : ""}`}
            ref={ref}
        >
            <Text textStyle={"body"} color="primary" as="span">
                {gettext("oos_product.previously_available_from")}
            </Text>

            <div className={styles.retailerListContainer}>
                {retailersList.map((retailer) => {
                    return (
                        <RetailerCard
                            key={retailer.retailer_name}
                            retailer={retailer}
                            productId={productId}
                        />
                    );
                })}
            </div>

            {Button && uniqueRetailerCount && uniqueRetailerCount > MIN_RETAILER_DISPLAY_COUNT && (
                <Button />
            )}
        </div>
    )
);

export interface PreviouslyAvailableFromWithModalProps {
    uniqueRetailers: OOSRetailerInfoSerializer[];
    productId: string;
}

export function PreviouslyAvailableFromWithModal({
    uniqueRetailers,
    productId,
}: PreviouslyAvailableFromWithModalProps): React.ReactElement {
    const visibleRef = useRef<HTMLDivElement | null>(null);
    const { isVisible } = useAboveFold(visibleRef);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const uniqueRetailerCount = uniqueRetailers.length;
    const firstThreeRetailers = useMemo(
        () => uniqueRetailers.slice(0, MIN_RETAILER_DISPLAY_COUNT),
        [uniqueRetailers]
    );

    useEffect(() => {
        if (isVisible) {
            analytics.event({
                category: "oos-previously-available",
                action: "impression",
                nonInteraction: true,
                customData: { product_id: productId },
            });
        }
    }, [isVisible, productId]);

    const OpenModalButton = useCallback(() => {
        const onClick = (): void => {
            analytics.event({
                category: "oos-previously-available-see-all",
                action: "clicked",
                customData: { product_id: productId },
            });
            setIsModalOpen(true);
        };
        return (
            <Button
                title={gettext("oos_product.previously_available_from.view_all_retailers")}
                variant="secondary"
                width="full"
                onClick={onClick}
                dataTestId="view-all-products-btn"
            />
        );
    }, [setIsModalOpen, productId]);

    return uniqueRetailerCount > 0 ? (
        <>
            <View width="full">
                <PreviouslyAvailableFrom
                    retailersList={firstThreeRetailers}
                    uniqueRetailerCount={uniqueRetailerCount}
                    productId={productId}
                    Button={OpenModalButton}
                    isModal={false}
                    ref={visibleRef}
                />
            </View>
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <PreviouslyAvailableFrom
                    retailersList={uniqueRetailers}
                    uniqueRetailerCount={uniqueRetailerCount}
                    productId={productId}
                    isModal
                />
            </Modal>
        </>
    ) : (
        <></>
    );
}
